import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { ToastrService } from "ngx-toastr";
import {
  getLoggedInUser,
  hasAiCreationAccess,
  hasContentCreationPlanAccess,
} from "src/app/shared/utils/plan.util";
import { trialAllowsGuidePacks } from "src/app/shared/utils/trial.util";
import { AuthModalService } from "src/app/Services/auth-modal.service";
import { PermissionsService } from "src/app/Services/permissions.service";
import { OrgContextService } from "src/app/Services/org-context.service";

@Injectable({ providedIn: "root" })
export class CreateContentGuard implements CanActivate {
  constructor(
    private router: Router,
    private toastr: ToastrService,
    private authModalService: AuthModalService,
    private permissionsService: PermissionsService,
    private orgContextService: OrgContextService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    const user = getLoggedInUser();
    if (!user) {
      this.authModalService.openModal("registration", state.url);
      return this.router.parseUrl("/");
    }

    // Organization account type → org access-role feature matrix.
    // Public account type → platform Flow/Bundle Create/Edit permission.
    if (
      this.orgContextService.snapshot.loaded &&
      this.orgContextService.isOrganizationMode()
    ) {
      if (!this.orgContextService.canCreateOrgContent()) {
        this.toastr.info(
          "Your organization role does not allow creating flows or bundles."
        );
        return this.router.parseUrl("/");
      }
    } else if (!this.permissionsService.hasFlowBundleCreateEditAccess(user)) {
      this.toastr.info(
        "Currently you do not have access to create flow/bundle please request access to admin"
      );
      return this.router.parseUrl("/");
    }

    // Selection preview lets users with role but without a plan see Smart Create + paid badge.
    if (route.data?.["allowWithoutPlan"] === true) {
      return true;
    }

    if (!hasContentCreationPlanAccess(user)) {
      this.toastr.info(
        "A Creator or Business plan is required to create flows and bundles.",
        "Subscription required"
      );
      return this.router.createUrlTree(["/pricing"]);
    }

    // Starter free trial: manual guides only (the API enforces the same limits).
    const trialGate = route.data?.["trialGate"];
    if (trialGate === "ai" && !hasAiCreationAccess(user)) {
      this.toastr.info(
        "AI creation isn't included in the Starter free trial. You can create your guide manually.",
        "Not included in your trial"
      );
      return this.router.createUrlTree(["/create-guide/manual"]);
    }
    if (trialGate === "guide-packs" && !trialAllowsGuidePacks()) {
      this.toastr.info(
        "Guide packs aren't included in the Starter free trial. Upgrade to create guide packs.",
        "Not included in your trial"
      );
      return this.router.createUrlTree(["/pricing"], { queryParams: { trial: "reminder" } });
    }

    return true;
  }
}
